<template>
  <div id="unregisteredNotificationsGrid">
    <v-dialog v-model="show" persistent>
      <v-card>
        <v-card-title
          :class="
            isMobile ? 'no-word-break-mobile' : 'text-h5 no-word-break-mobile'
          "
        >
          <v-container fluid>
            <v-row no-gutters align="center" justify="space-between">
              <v-col cols="12" md="6" class="d-flex align-center flex-wrap">
                <v-icon left large :color="iconColor">{{ icon }}</v-icon>
                <span :style="{ marginRight: '8px' }">{{ title }}</span>
                <!-- por configuracion N ultimos dias a mostrar -->
                <span
                  v-if="configuration"
                  class="my-0 subtitle-2 pt-1 pb-0 px-2 rounded d-inline-block d-sm-block w-auto"
                  :style="{
                    backgroundColor: $vuetify.theme.dark
                      ? '#2e7d32'
                      : '#e8f5e9', // verde
                    display: 'inline-block',
                  }"
                >
                  {{
                    $t('periods.lastConfiguredPeriod', {
                      amount: configuration.loadLastDays,
                      unit: $t('periods.days'),
                    })
                  }}
                </span>
              </v-col>

              <v-col cols="12" md="6" class="mt-4 mt-md-0">
                <v-row no-gutters align="center" justify="end">
                  <v-col cols="10" md="8">
                    <v-text-field
                      data-v-step="104"
                      class="my-0 py-0"
                      v-model="search"
                      @change="searchChange"
                      @focus="isSearchFocused = true"
                      @blur="isSearchFocused = false"
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      :label="searchLabel"
                      hide-details
                    >
                    </v-text-field>
                  </v-col>

                  <v-col
                    cols="2"
                    md="2"
                    class="text-right"
                    v-if="
                      isSuperAdminUser ||
                      isSuperOperatorUser ||
                      isAdminAndCanManageCompanies
                    "
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          elevation="1"
                          fab
                          small
                          class="primary secondary--text ma-1"
                          v-bind="attrs"
                          v-on="on"
                          id="export-data"
                          @click.stop="exportNotifications"
                        >
                          <v-icon>mdi-file-export</v-icon>
                        </v-btn>
                      </template>
                      <div>
                        {{ $t('notificationActions.title.export') }}
                      </div>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- mensaje de última actualización de notificaciones no registradas y botón reload  -->
            <v-row
              class="mt-0"
              align="baseline"
              v-if="$store.state.user.lastRetrievedNotificationsUnregistered"
            >
              <span style="color: grey; font-size: 14px" class="pl-2 pr-1 mt-0">
                {{ $t('lastUpdated') }}:
                {{
                  $d(
                    $store.state.user.lastRetrievedNotificationsUnregistered.toDate(),
                    'i18nDateAndHour'
                  )
                }}
              </span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    style="text-transform: none"
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="reload()"
                    :disabled="$store.state.user.needsCertEmpresa || loading"
                  >
                    <v-icon v-if="!loading"> mdi-reload </v-icon>
                    <v-icon v-else> mdi-autorenew mdi-spin </v-icon>
                  </v-btn>
                </template>
                <div>
                  {{ $t('refreshNow') }}
                </div>
              </v-tooltip>
            </v-row>
          </v-container>
        </v-card-title>

        <UnregisteredNotificationsTable
          :loading="false"
          :headers="headers"
          :mobileHeaders="mobileHeaders"
          :notifications="filteredNotifications"
          ref="unregisteredNotificationsTableRef"
          :cifs="cifs"
        ></UnregisteredNotificationsTable>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="close">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UnregisteredNotificationsTable from '@/components/notifications/UnregisteredNotificationsTable'
import {
  getNotificationStatus,
  getNotificationType,
} from '@/services/notification-service'
import { normalizeSearch } from '@/utils/string-utils'
import { mapGetters, mapMutations } from 'vuex'
const lodash = require('lodash')

export default {
  components: {
    UnregisteredNotificationsTable,
  },
  props: {
    showDialog: Boolean,
    notificationsUnregistered: {
      type: Array,
      default: () => [],
    },
    cifs: Array,
    title: String,
    icon: String,
    iconColor: String,
    configuration: Object,
  },
  data() {
    return {
      headers: [
        'id',
        'concepto',
        'nif_titular',
        'nombre_titular',
        'fecha_puesta_disposicion',
        'identificador',
        'estado',
        'nombre_organismo_emisor',
        'tipo_envio',
        'codigo_organismo_emisor',
        'nombre_organismo_emisor_raiz',
        'codigo_organismo_emisor_raiz',
      ],
      mobileHeaders: [
        'id',
        'concepto',
        'nif_titular',
        'nombre_titular',
        'fecha_puesta_disposicion',
        'identificador',
        'estado',
        'nombre_organismo_emisor',
      ],
      mustBeShow: this.showDialog,
      loading: false,
      notifications: [],
      search: '',
      debouncedSearch: '',
      handleDebouncedSearch: null,
      filteredNotifications: [],
      isSearchFocused: false,
    }
  },
  mounted() {
    this.notifications = [...this.notificationsUnregistered]

    // Para la busqueda 'normal':
    // Espera 300 ms tras pulsar la ultima tecla. Asi no se sobrecarga la CPU mientras el usuario escribe
    // nota: se usa .bind(this) para asegurar que 'this' dentro de la función del debounce sigue
    // apuntando al componente Vue.
    this.handleDebouncedSearch = lodash.debounce(
      function (val) {
        this.debouncedSearch = val
      }.bind(this),
      300
    )
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'isOperatorUser',
      'isAdminUser',
      'isSuperAdminUser',
      'isSuperOperatorUser',
      'isAdminAndCanManageCompanies',
    ]),
    show() {
      return this.showDialog
    },
    unregisteredNotificationParsed() {
      const arr = this.notificationsUnregistered || []
      return arr.map((n) => ({
        ...n,
        fecha_puesta_disposicion: new Date(n.fecha_puesta_disposicion),
        estado: n.origen === 'DEHUC' ? 'Comparecida' : '',
      }))
    },
    searchLabel() {
      return this.isSearchFocused
        ? this.$t('searches.searchHelp')
        : this.$t('searchField')
    },
    searchTerms() {
      // Ahora se permite realizar una búsqueda de varias palabras separadas por comas
      return this.debouncedSearch
        ? this.debouncedSearch
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .map(normalizeSearch) // normalización para eliminar acentos
            .filter((term) => typeof term === 'string' && term.length > 0)
        : []
    },
  },
  methods: {
    ...mapMutations([
      'setSnackbar',
      'setNotificationsSearch' /*,'setUnregisteredNotificationsReload'*/,
    ]),
    close() {
      this.mustBeShow = false
      this.$emit('dialog-closed')
    },
    reload() {
      this.loading = true
      this.$refs.unregisteredNotificationsTableRef.clearTable()
      //this.setUnregisteredNotificationsReload()
      this.$store.commit('setUnregisteredNotificationsReload')
    },
    searchChange(search) {
      this.setNotificationsSearch(search ? search : '') //If clearable, store empty string instead of null
    },
    updateFilteredNotifications() {
      const notifications = Array.isArray(this.notifications)
        ? this.notifications
        : []
      const terms = this.searchTerms

      if (!this.search || terms.length === 0) {
        this.filteredNotifications = notifications
        return
      }

      this.filteredNotifications = notifications.filter((item) => {
        const {
          codigo_organismo_emisor,
          codigo_organismo_emisor_raiz,
          concepto,
          fecha_puesta_disposicion,
          id,
          identificador,
          nif_titular,
          nombre_organismo_emisor,
          nombre_organismo_emisor_raiz,
          nombre_titular,
          tipo_envio,
        } = item

        // Guardamos los elementos en mayúscula
        let dataCap = [
          this.$t(getNotificationStatus(item).status).toUpperCase(),
          codigo_organismo_emisor?.toUpperCase(),
          codigo_organismo_emisor_raiz?.toUpperCase(),
          concepto?.toUpperCase(),
          this.$d(fecha_puesta_disposicion, 'i18nDate').toUpperCase(),
          id?.toUpperCase(),
          identificador?.toUpperCase(),
          nif_titular?.toUpperCase(),
          nombre_organismo_emisor?.toUpperCase(),
          nombre_organismo_emisor_raiz?.toUpperCase(),
          nombre_titular?.toUpperCase(),
          this.$t(getNotificationType({ tipo_envio }).name).toUpperCase(),
        ]

        // filtramos y normalizamos los atributos de tipo string para eliminar acentos
        dataCap = dataCap
          .filter((val) => typeof val === 'string' && val.length > 0)
          .map(normalizeSearch)

        // Comprueba si alguno de los campos contiene alguno de los términos de búsqueda,
        // optimizado porque se detiene en el primero que encuentre y no continua buscando.
        return dataCap.some((field) =>
          this.searchTerms.some((term) => field.includes(term))
        )
      })
    },

    exportNotifications() {
      var exportNotifications = this.filteredNotifications.map(
        (notification) => {
          return {
            identificador: notification.identificador,
            estado: notification.estado,
            concepto: notification.concepto,
            fecha_puesta_disposicion:
              notification.fecha_puesta_disposicion.toLocaleDateString(
                this.$i18n.locale,
                {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                }
              ),
            nif_titular: notification.nif_titular,
            nombre_titular: notification.nombre_titular,
            nif_peticion: notification.nif_peticion,
            nombre_organismo_emisor: notification.nombre_organismo_emisor,
            codigo_organismo_emisor: notification.codigo_organismo_emisor,
            nombre_organismo_emisor_raiz:
              notification.nombre_organismo_emisor_raiz,
            codigo_organismo_emisor_raiz:
              notification.codigo_organismo_emisor_raiz,
          }
        }
      )
      if (exportNotifications.length < 1) {
        const message = this.$t('nothingToExport')
        this.setSnackbar({ position: 'top', type: 'info', message })
        return
      }
      const replacer = (key, value) => (value === null ? '' : value) // valores undefined

      //header auxiliar con el nombre de las variables
      const header = [
        'identificador',
        'estado',
        'concepto',
        'fecha_puesta_disposicion',
        'nif_titular',
        'nombre_titular',
        'nif_peticion',
        'nombre_organismo_emisor',
        'codigo_organismo_emisor',
        'nombre_organismo_emisor_raiz',
        'codigo_organismo_emisor_raiz',
      ]
      //header a mostrar en el csv
      const header2 = [
        this.$i18n.t('csv.notifications.identificador'),
        this.$i18n.t('csv.notifications.estado'),
        this.$i18n.t('csv.notifications.concepto'),
        this.$i18n.t('csv.notifications.fecha_puesta_disposicion'),
        this.$i18n.t('csv.notifications.nif_titular'),
        this.$i18n.t('csv.notifications.nombre_titular'),
        this.$i18n.t('csv.notifications.nif_peticion'),
        this.$i18n.t('csv.notifications.nombre_organismo_emisor'),
        this.$i18n.t('csv.notifications.codigo_organismo_emisor'),
        this.$i18n.t('csv.notifications.nombre_organismo_emisor_raiz'),
        this.$i18n.t('csv.notifications.codigo_organismo_emisor_raiz'),
      ]

      const csv = [
        header2.join(';'), // asignamos separador
        ...exportNotifications.map((row) =>
          header
            .map((fieldName) => JSON.stringify(row[fieldName], replacer))
            .join(';')
        ),
      ].join('\r\n')
      // Creamos elemento html
      const link = document.createElement('a')
      // Creamos el objeto blob con el contenido deseado (el array de objetos pasado a cadena de texto en formato csv)
      const file = new Blob([csv], { type: 'text/plain' })
      // añadimos el objeto a la url
      link.href = URL.createObjectURL(file)
      // le damos nombre
      link.download = 'unregisteredNotifications.csv'
      // Se descarga el objeto forzando un click en el elemento y eliminamos la url.
      link.click()
      URL.revokeObjectURL(link.href)
      const message = this.$t('exportComplete')
      this.setSnackbar({ position: 'top', type: 'success', message })
      return
    },
  },
  watch: {
    notificationsUnregistered: {
      immediate: true,
      handler() {
        this.notifications = this.unregisteredNotificationParsed
        // para cuando termine el reload
        this.loading = false
      },
    },
    notifications: 'updateFilteredNotifications',
    debouncedSearch: 'updateFilteredNotifications',
    search(val) {
      // cada vez que el usuario escriba una letra llama a la funcion de retardo
      this.handleDebouncedSearch(val)
    },
  },
}
</script>

<style lang="scss" scoped>
@media (max-width: 600px) {
  .no-word-break-mobile {
    word-break: keep-all;
    overflow-wrap: normal; /* por si acaso */
  }
}
</style>
